import { BadRequestException } from '@zaoblako/shared/common/exceptions/bad-request.exception';
import { InternalServerErrorException } from '@zaoblako/shared/common/exceptions/internal-server-error.exception';
import { SerdeDtoRegistryClass } from '@zaoblako/shared/modules/serde/classes/serde-dto-registry.class';
import { SerdeContextProviderClass } from '@zaoblako/shared/modules/serde/classes/serde-context-provider.class';
import { SerdeCheckersRegistryClass } from '@zaoblako/shared/modules/serde/classes/serde-checkers-registry.class';
import { SerdeTransformTypeEnum } from '@zaoblako/shared/modules/serde/enums/serde-transform-type.enum';
import { SerdeEnvironmentEnum } from '@zaoblako/shared/modules/serde/enums/serde-environment.enum';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import type { BaseHttpException } from '@zaoblako/shared/common/exceptions/base-http.exception';
import type { PrimitiveKeyInterface } from '@zaoblako/shared/common/interfaces/primitive-key.interface';
import type { SerdeContextDataInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context-data.interface';
import type { SerdeDtoSchemaInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-dto-schema.interface';
import type { SerdeContextInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context.interface';
import type { SerdeCheckerInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-checker.interface';
import type { SerdeDtoMetadataInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-dto-metadata.interface';
import type { SerdeFieldMetadataInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-field-metadata.interface';
import type { SerdeTransformFunctionInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-transform-function.interface';

import * as yup from 'yup';
import { convertSchema } from '@sodaru/yup-to-json-schema';
import { mapValues, isFunction, isNil, isEmpty, uniq, trim, camelCase, upperFirst } from 'lodash';
import { unflatten } from 'flat';
import type { OpenAPIV3_1 as OpenAPI } from 'openapi-types';

export class SerdeDtoClass<T, D extends SerdeContextDataInterface = SerdeContextDataInterface> {
  public static readonly DEFAULT_MAX_RECURSION_DEPTH = 2;

  /**
   * Get selectable properties for DTO.
   * This method can be used to retrieve default values for selectable properties field.
   *
   * @template T Type of DTO
   *
   * @param target DTO instance or unique key
   *
   * @returns Transform callback ready to apply
   */
  public static getSelectableProperties<T>(target: SerdeDtoClass<T> | PrimitiveKeyInterface,): (keyof T)[] {
    let dto: SerdeDtoClass<T>;

    if (target instanceof SerdeDtoClass) {
      dto = target;
    } else {
      const registry = SerdeDtoRegistryClass.getInstance();
      if (!registry.hasDto(target)) {
        throw new Error(`DTO with key "${String(target)}" not found`);
      }
      dto = registry.getDto(target);
    }

    return Object.keys(dto.schema) as (keyof T)[];
  }

  /**
   * Get selectable properties validator for DTO
   *
   * @template T Type of DTO
   *
   * @param target DTO instance or unique key
   *
   * @returns Transform callback ready to apply
   */
  public static getSelectablePropertiesValidator<T>(
    target: SerdeDtoClass<T> | PrimitiveKeyInterface,
  ): SerdeTransformFunctionInterface<(keyof T)[], any> {
    return (): yup.Schema<any> => {
      let dto: SerdeDtoClass<T>;

      if (target instanceof SerdeDtoClass) {
        dto = target;
      } else {
        const registry = SerdeDtoRegistryClass.getInstance();
        if (!registry.hasDto(target)) {
          throw new Error(`DTO with key "${String(target)}" not found`);
        }
        dto = registry.getDto(target);
      }

      const keys = Object.keys(dto.schema)
      const message = `Допускаются только следующие поля для выбора: ${
        keys
          .map((key) => `"${key}" (${dto.schema[<keyof T>key].label})`)
          .join(', ')
      }`;
      return yup.array(
        yup
          .string()
          .oneOf(keys, message),
      );
    };
  }

  /**
   * Get sorting criteria validator for DTO
   *
   * @template D Context data
   *
   * @param target DTO instance or unique key
   * @param [maxDepth] = Max nested properties depth
   * @param [alias] Alias for fields
   *
   * @returns Transform callback ready to apply
   */
  public static getSortingCriteriaValidator(
    target: SerdeDtoClass<any> | PrimitiveKeyInterface,
    maxDepth?: number,
    alias?: string
  ): SerdeTransformFunctionInterface<string, any> {
    if (target instanceof SerdeDtoClass) {
      return (context): yup.StringSchema => target.getSortingCriteriaValidator(context.data, alias, maxDepth);
    }

    return (context): yup.StringSchema => {
      const registry = SerdeDtoRegistryClass.getInstance();
      if (!registry.hasDto(target)) {
        throw new Error(`DTO with key "${String(target)}" not found`);
      }

      return registry
        .getDto(target)
        .getSortingCriteriaValidator(context.data, alias, maxDepth);
    };
  }

  /**
   * Create and register new DTO
   *
   * @param key DTO unique key
   * @param schema DTO schema object
   *
   * @returns DTO ready to use
   */
  public static create<T, D extends SerdeContextDataInterface = SerdeContextDataInterface>(
    key: PrimitiveKeyInterface,
    schema: SerdeDtoSchemaInterface<T, D>,
  ): SerdeDtoClass<T> {
    const registry = SerdeDtoRegistryClass.getInstance();
    if (registry.hasDto(key)) {
      throw new Error(`DTO with key "${String(key)}" already exists`);
    }

    const dto = new SerdeDtoClass<T, D>(key, schema);
    registry.addDto(key, dto);
    return dto;
  }

  /**
   * Create and register new DTO based on other DTO
   *
   * @param parent Other DTO instance or unique key
   * @param key DTO unique key
   * @param schema DTO schema object (without parent DTO schema data)
   *
   * @returns DTO ready to use
   */
  public static from<P, C extends P, D extends SerdeContextDataInterface = SerdeContextDataInterface>(
    parent: SerdeDtoClass<P> | PrimitiveKeyInterface,
    key: PrimitiveKeyInterface,
    schema: SerdeDtoSchemaInterface<Omit<C, keyof P>, D>,
  ): SerdeDtoClass<C> {
    const registry = SerdeDtoRegistryClass.getInstance();
    if (registry.hasDto(key)) {
      throw new Error(`DTO with key "${String(key)}" already exists`);
    }

    let source: SerdeDtoClass<P, D>;
    if (parent instanceof SerdeDtoClass) {
      source = parent;
    } else {
      if (!registry.hasDto(parent)) {
        throw new Error(`DTO with key "${String(parent)}" not found`);
      }
      source = registry.getDto(parent);
    }

    const dto = new SerdeDtoClass<C, D>(key, {
      ...<any>source.schema,
      ...<any>schema,
    });
    registry.addDto(key, dto);

    return dto;
  }

  /**
   * Create and register DTO which is union of the other DTOs
   *
   * @param key DTO unique key
   * @param sources Schemas or unique keys list of DTOs to unite
   *
   * @returns United DTO
   */
  public static union<T, D extends SerdeContextDataInterface = SerdeContextDataInterface>(
    key: PrimitiveKeyInterface,
    sources: (SerdeDtoClass<any> | PrimitiveKeyInterface)[],
  ): SerdeDtoClass<T, D> {
    const registry = SerdeDtoRegistryClass.getInstance();
    if (registry.hasDto(key)) {
      throw new Error(`DTO with key "${String(key)}" already exists`);
    }

    // Find all source schemas and build union schema
    const schema: SerdeDtoSchemaInterface<T, D> = <any>{};
    for (const source of sources) {
      let found: SerdeDtoClass<any>;
      if (source instanceof SerdeDtoClass) {
        found = source;
      } else {
        if (!registry.hasDto(source)) {
          throw new Error(`DTO with key "${String(source)}" not found`);
        }
        found = registry.getDto(source);
      }
      Object.assign(schema, found.schema);
    }

    // Create and register DTO instance
    const dto = new SerdeDtoClass<T, D>(key, schema);
    registry.addDto(key, dto);

    return dto;
  }

  /**
   * Make "partial" version of the DTO: all properties will be optional
   *
   * @param key DTO unique key
   * @param source Origin DTO instance or unique key
   *
   * @returns Partial verision of the DTO
   */
  public static partial<T, D extends SerdeContextDataInterface = SerdeContextDataInterface>(
    key: PrimitiveKeyInterface,
    source: SerdeDtoClass<T, D> | PrimitiveKeyInterface
  ): SerdeDtoClass<Partial<T>, D> {
    const registry = SerdeDtoRegistryClass.getInstance();
    if (registry.hasDto(key)) {
      throw new Error(`DTO with key "${String(key)}" already exists`);
    }

    let origin: SerdeDtoClass<T, D>;
    if (source instanceof SerdeDtoClass) {
      origin = source;
    } else {
      if (!registry.hasDto(source)) {
        throw new Error(`DTO with key "${String(key)}" not found`);
      }
      origin = registry.getDto(source);
    }

    const dto = new SerdeDtoClass<T, D>(
      key,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      mapValues(origin.schema, ({ optional, hidden, default: _, ...rest }) => (<any>{
        ...rest,
        optional: true,
        hidden: false,
      })),
    );
    registry.addDto(key, dto);

    return dto;
  }

  /**
   * Get unique DTO key in OpenAPI schema
   *
   * @param dto DTO instance or unique key
   *
   * @returns DTO key in OpenAPI schema (`string` value)
   */
  public static getOpenApiSchemaKey(dto: SerdeDtoClass<any, any> | PrimitiveKeyInterface): string {
    const key = dto instanceof SerdeDtoClass
      ? String(dto.key)
      : String(dto);

    return key
      .split('/')
      .map(
        (item, index, chunks) => index > 0 && chunks.length > 1
          ? upperFirst(camelCase(item))
          : camelCase(item),
      )
      .join('.');
  }

  /**
   * Get DTO reference object in OpenAPI schema
   *
   * @param dto DTO instance or unique key
   *
   * @returns DTO reference object in OpenAPI schema (object with `$ref` key)
   */
  public static getOpenApiSchemaRef(dto: SerdeDtoClass<any, any> | PrimitiveKeyInterface): OpenAPI.ReferenceObject {
    return {
      $ref: `#/components/schemas/${SerdeDtoClass.getOpenApiSchemaKey(dto)}`,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor(
    private readonly key: PrimitiveKeyInterface,
    private readonly schema: SerdeDtoSchemaInterface<T, D>,
  ) {}

  /** Get DTO unique key */
  public getKey(): PrimitiveKeyInterface {
    return this.key;
  }

  /**
   * Deserialize and validate value to DTO **sync** (without async validations) and **unsafely** (with error throwing)
   *
   * @param value Value to transform and validate
   * @param [contextData] Additional context data
   *
   * @throws Validation error if validation failed
   *
   * @returns Deserialized DTO instance
   */
  public validateSyncUnsafe = (value: unknown, contextData?: D): T => {
    const [result, err] = this.validateSyncSafe(value, contextData);
    if (err) {
      throw err;
    }
    return result;
  };

  /**
   * Deserialize and validate value to DTO **async** (with async validations) and **unsafely** (with error throwing)
   *
   * @param value Value to transform and validate
   * @param [contextData] Additional context data
   *
   * @throws Validation error if validation failed
   *
   * @returns `Promise` with deserialized DTO instance
   */
  public validateAsyncUnsafe = async (value: unknown, contextData?: D): Promise<T> => {
    const [result, err] = await this.validateAsyncSafe(value, contextData);
    if (err) {
      throw err;
    }
    return result;
  };

  /**
   * Deserialize and validate value to DTO **sync** (without async validations) and **safely** (without error throwing)
   *
   * @param value Value to transform and validate
   * @param [contextData] Additional context data
   *
   * @returns A tuple where:
   *   - First element is deserialized DTO instance (or `null` if validation failed);
   *   - Second element is validation error (or `null` if validation succeed);
   */
  public validateSyncSafe = (value: unknown, contextData?: D): [T, BaseHttpException<T>] => {
    const baseContext = SerdeContextProviderClass
      .getInstance()
      .getBaseContext(SerdeTransformTypeEnum.DESERIALIZE);
    const context: SerdeContextInterface<D> = {
      value,
      environment: baseContext.environment,
      transform: baseContext.transform,
      data: {
        ...baseContext.data,
        ...(contextData || <any>{}),
      },
    };

    // Transform and validate value
    let result: T = null;
    let error: BaseHttpException = null;
    try {
      const validator = this.createValidator(context, false);
      result = validator.validateSync(value);
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        error = new BadRequestException('Ошибка валидации');
        error.validationMetadata = this.getMetadata(contextData);
        error.validationErrors = unflatten({
          [(<yup.ValidationError>err).path]: (<yup.ValidationError>err).errors.join('\n')
        });
      } else {
        error = new InternalServerErrorException(err?.message || String(err), { cause: err });
      }
    }

    return [result, error];
  };

  /**
   * Deserialize and validate value to DTO **async** (with async validations) and **safely** (without error throwing)
   *
   * @param value Value to transform and validate
   * @param [contextData] Additional context data
   *
   * @returns `Promise` with tuple where:
   *   - First element is deserialized DTO instance (or `null` if validation failed);
   *   - Second element is validation error (or `null` if validation succeed);
   */
  public validateAsyncSafe = async (value: unknown, contextData?: D): Promise<[T, BaseHttpException<T>]> => {
    const baseContext = SerdeContextProviderClass
      .getInstance()
      .getBaseContext(SerdeTransformTypeEnum.DESERIALIZE);
    const context: SerdeContextInterface<D> = {
      value,
      environment: baseContext.environment,
      transform: baseContext.transform,
      data: {
        ...baseContext.data,
        ...(contextData || <any>{}),
      },
    };

    // Transform and validate value
    let result: T = null;
    let error: BaseHttpException = null;
    try {
      const validator = this.createValidator(context, true);
      result = await validator.validate(value, { stripUnknown: true });
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        error = new BadRequestException('Ошибка валидации');
        error.validationMetadata = this.getMetadata(contextData);
        error.validationErrors = unflatten({
          [(<yup.ValidationError>err).path]: (<yup.ValidationError>err).errors.join('\n')
        });
      } else {
        error = new InternalServerErrorException(err?.message || String(err), { cause: err });
      }
    }

    return [result, error];
  };

  /**
   * Transform raw values list to DTO instances list without validation
   *
   * @param values Values to transform
   * @param [contextData] Additional context data
   *
   * @returns Deserialized DTO instances list
   */
  public deserializeList = (values: unknown[], contextData?: D): T[] => values.map(value => this.deserialize(value, contextData));

  /**
   * Transform raw value to DTO instance without validation
   *
   * @param value Value to transform
   * @param [contextData] Additional context data
   *
   * @returns Deserialized DTO instance
   */
  public deserialize = (value: unknown, contextData?: D): T => {
    const baseContext = SerdeContextProviderClass
      .getInstance()
      .getBaseContext(SerdeTransformTypeEnum.DESERIALIZE);
    const context: SerdeContextInterface<D> = {
      value,
      environment: baseContext.environment,
      transform: baseContext.transform,
      data: {
        ...baseContext.data,
        ...(contextData || <any>{}),
      },
    };

    return this
      .createValidator(context, false)
      .cast(value, {
        stripUnknown: true,
        assert: false,
      });
  };

  /**
   * Transform list of DTO instances to raw values list
   *
   * @param values Values list to transform
   * @param [contextData] Additional context data
   *
   * @returns Deserialized DTO instances list
   */
  public serializeList = (values: T[], contextData?: D): any[] => values.map(value => this.serialize(value, contextData));

  /**
   * Transform DTO instance to raw value
   *
   * @param value Value to transform
   * @param [contextData] Additional context data
   *
   * @returns Deserialized DTO instance
   */
  public serialize = (value: T, contextData?: D, cast?: boolean): any => {
    if (cast) {
      const baseContext = SerdeContextProviderClass
        .getInstance()
        .getBaseContext(SerdeTransformTypeEnum.SERIALIZE);
      const context: SerdeContextInterface<D> = {
        value,
        environment: baseContext.environment,
        transform: baseContext.transform,
        data: {
          ...baseContext.data,
          ...(contextData || <any>{}),
        },
      };

      return this
        .createValidator(context, false)
        .cast(value, {
          stripUnknown: true,
          assert: false,
          resolved: true,
        });
    } else {
      return value;
    }
  };

  /**
   * Get DTO metadata:
   *   - Fields labels and hints;
   *   - Optional and flags;
   *
   * @param [contextData] Additional context data
   */
  public getMetadata = (contextData?: D, maxDepth?: number): SerdeDtoMetadataInterface<T> => {
    return this.getMetadataInternal(maxDepth ?? SerdeDtoClass.DEFAULT_MAX_RECURSION_DEPTH, contextData);
  };

  /**
   * Build and return OpenAPI v3.1.0 schema for DTO.
   * This schema can be used by "Swagger"
   *
   * @returns OpenAPI v3.1.0 schema
   */
  public asOpenApiSchema(): OpenAPI.SchemaObject {
    const result: OpenAPI.SchemaObject = {
      required: [],
      properties: {},
    };

    const context = SerdeContextProviderClass
        .getInstance()
        .getBaseContext(SerdeTransformTypeEnum.SERIALIZE) as SerdeContextInterface<D>;

    result.properties = mapValues(this.schema, (item, key): OpenAPI.SchemaObject | OpenAPI.ReferenceObject => {
      const label = isFunction(item.label) ? item.label(<SerdeContextInterface<D>>context) : item.label;
      let hint: string;
      if (item.hint) {
        hint = isFunction(item.hint) ? item.hint(context) : item.hint;
      }

      const optional = isFunction(item.optional) ? item.optional(context) : Boolean(item.optional);
      if (!optional) {
        result.required.push(key);
      }

      if (item.nested) {
        return <OpenAPI.ReferenceObject>{
          ...SerdeDtoClass.getOpenApiSchemaRef(item.nested),
          summary: label,
          description: hint,
        };
      }

      const defaultValue: any = isFunction(item.default) ? item.default(context) : item.default;

      let yupSchema: yup.Schema<any> = isFunction(item.serializer) ? item.serializer(context) : item.serializer;
      if (optional) {
        yupSchema = yupSchema.notRequired().default(defaultValue)
      } else if (defaultValue) {
        yupSchema = yupSchema.default(defaultValue);
      }

      const jsonSchema: OpenAPI.SchemaObject = convertSchema(yupSchema) as any;
      jsonSchema.title = label;
      jsonSchema.description = `### ${label}\n\n${hint || 'Без описания'}`;

      return jsonSchema;
    });

    return result;
  }

  /**
   * Get special validator which for sort setting string in `[+-]<field>` format
   *
   * @param [contextData] Additional context data
   * @param [alias] Alias for fields
   * @param [maxDepth] Max nested properties depth
   *
   * @returns `yup` validation schema ready to use
   */
  private getSortingCriteriaValidator = (contextData?: D, alias?: string, maxDepth?: number): yup.StringSchema => {
    const metadata = this.getMetadata(contextData, maxDepth ?? SerdeDtoClass.DEFAULT_MAX_RECURSION_DEPTH);
    const criterias = uniq(this.getSortingCriteriasFromMetadata(maxDepth ?? SerdeDtoClass.DEFAULT_MAX_RECURSION_DEPTH, metadata, alias ?? '', 0));
    if (isEmpty(criterias)) {
      throw new Error(`DTO "${String(this.key)}" and nested DTOs has no sortable fields`);
    }
    return yup
      .string()
      .test({
        name: 'sorting-criteria',
        message: ({ value }) => `Недопустимый критерий сортировки: "${
          value
        }". Допустимые значения: ${
          criterias
            .map(criteria => `"${criteria}"`)
            .join(', ')
        }`,
        test: (value) => {
          if (!value) {
            return true;
          }
          const [field] = String(value).split(':');
          return criterias.some(criteria => field.startsWith(criteria));
        },
      })
  };

  private getSortingCriteriasFromMetadata(maxDepth: number, metadata: SerdeDtoMetadataInterface<T>, alias?: string, level?: number): string[] {
    if (level > maxDepth) {
      return [];
    }

    const fields: string[] = [];
    for (const [key, { sortable, nested }] of Object.entries<SerdeFieldMetadataInterface>(metadata)) {
      if (!sortable) {
        continue;
      }

      if (nested) {
        fields.push(...this.getSortingCriteriasFromMetadata(maxDepth, nested, `${alias ?? ''}.${key}`, level + 1));
      } else {
        fields.push(`${alias ?? ''}.${key}`);
      }
    }

    return fields
      .map(field => {
        const clear = trim(trim(trim(field, '.'), '+'), '-');
        return [
          clear,
          `+${clear}`,
          `-${clear}`,
        ];
      })
      .flat()
  }

  /**
   * Get DTO metadata:
   *   - Fields labels and hints;
   *   - Optional and flags;
   *
   * @param maxDepth Max metadata recursive retrieve depth
   * @param [contextData] Additional context data
   * @param [upContext] Context, computed by up DTOs. If it's defined, then it will be used instead of new context.
   * @param [upDtos] Up DTOs set. Need to handle recursive DTOs
   */
  private getMetadataInternal(
    maxDepth: number,
    contextData?: D,
    upContext?: SerdeContextInterface<D>,
    upDtos: SerdeDtoClass<any>[] = [this],
  ): SerdeDtoMetadataInterface<T> {
    if (!isNil(maxDepth) && ((upDtos.length - 1) > maxDepth)) {
      return <any>{};
    }

    let context: SerdeContextInterface<D> = upContext;
    if (!upContext) {
      const baseContext = SerdeContextProviderClass
        .getInstance()
        .getBaseContext(SerdeTransformTypeEnum.DESERIALIZE);
      context = {
        value: null,
        environment: baseContext.environment,
        transform: baseContext.transform,
        data: {
          ...baseContext.data,
          ...(contextData || <any>{}),
        },
      };
    }

    return mapValues(this.schema, item => {
      const result: SerdeFieldMetadataInterface = {
        label: isFunction(item.label) ? item.label(context) : item.label,
      };
      if (item.hint) {
        result.hint = isFunction(item.hint) ? item.hint(context) : item.hint;
      }
      if (item.optional) {
        result.optional = isFunction(item.optional) ? item.optional(context) : item.optional;
      }
      if (item.hidden) {
        result.hidden = isFunction(item.hidden) ? item.hidden(context) : item.hidden;
      }
      if (item.disabled) {
        result.disabled = isFunction(item.disabled) ? item.disabled(context) : item.disabled;
      }
      if (item.sortable) {
        result.sortable = isFunction(item.sortable) ? item.sortable(context) : item.sortable;
      }
      if (item.nested) {
        let nested: SerdeDtoClass<any>;
        if (item.nested instanceof SerdeDtoClass) {
          nested = item.nested;
        } else {
          const registry = SerdeDtoRegistryClass.getInstance();
          if (!registry.hasDto(item.nested)) {
            throw new Error(`DTO with key "${String(item.nested)}" not found`);
          }
          nested = registry.getDto(item.nested);
        }
        if (!upDtos.includes(nested)) {
          result.nested = nested.getMetadataInternal(maxDepth, contextData, context, [...upDtos, nested]);
        }
      }
      return result;
    });
  }

  /**
   * Create `yup` validator for DTO
   *
   * @param context Validation context
   * @param async If `true` then on backend environment backend async checkers will be applied
   *
   * @returns `yup` validation schema ready to use
   */
  private createValidator(
    context: SerdeContextInterface<D>,
    async: boolean,
    upDtos: SerdeDtoClass<any>[] = [this],
  ): yup.ObjectSchema<any> {
    const shape: yup.ObjectShape = mapValues(this.schema, (item, key) => {
      // Resolve nested DTO
      let nested: SerdeDtoClass<any, any> = null;
      if (item.nested) {
        if (item.nested instanceof SerdeDtoClass) {
          nested = item.nested;
        } else {
          const registry = SerdeDtoRegistryClass.getInstance();
          if (!registry.hasDto(item.nested)) {
            throw new Error(`DTO with key "${String(item.nested)}" not found`);
          }
          nested = registry.getDto(item.nested);
        }
      }

      // Resolve optional flag
      let optional = false;
      if (!isNil(item.optional)) {
        optional = isFunction(item.optional) ? item.optional(context) : item.optional;
      }

      // Resolve validation schema
      let schema: yup.Schema<any> = null;
      if (yup.isSchema(item.serializer)) {
        schema = optional ? (<yup.Schema>item.serializer).notRequired().default(undefined) : (<yup.Schema>item.serializer).required(DtoMessageEnum.REQUIRED_FIELD)
        if (!isNil(item.default)) {
          schema = schema.default(isFunction(item.default) ? item.default(context) : item.default);
        }
      } else {
        let nestedValidator = null;
        if (nested) {
          if (upDtos.includes(nested)) {
            nestedValidator = yup.lazy(() => {
              const validator = nested
                .createValidator(
                  {
                    ...context,
                    value: context?.value?.[key],
                  },
                  async,
                  [...upDtos, nested]
                )
                .default(undefined);
              return optional ? validator.notRequired() : validator.required(DtoMessageEnum.REQUIRED_FIELD);
            });
          } else {
            const validator = nested.createValidator(
              {
                ...context,
                value: context?.value?.[key],
              },
              async,
              [...upDtos, nested],
            )
            nestedValidator = optional ? validator.notRequired().default(undefined) : validator.required(DtoMessageEnum.REQUIRED_FIELD);
          }
        }
        schema = (<SerdeTransformFunctionInterface<any>>item.serializer)(context, nestedValidator);
        if (!nested) {
          schema = optional ? schema.notRequired().default(undefined) : schema.required(DtoMessageEnum.REQUIRED_FIELD);
        }
        if (!isNil(item.default)) {
          schema = schema.default(() => isFunction(item.default) ? item.default(context) : item.default);
        }
      }
      if (!schema) {
        throw new Error('Could not yup schema for DTO');
      }

      // Apply backend checks on backend environments
      if (async && context.environment === SerdeEnvironmentEnum.BACKEND && !isEmpty(item.checkers)) {
        const registry = SerdeCheckersRegistryClass.getInstance();
        for (const [index, sourceChecker] of item.checkers.entries()) {
          let checker: SerdeCheckerInterface<any, D>;
          if (isFunction(sourceChecker)) {
            checker = <SerdeCheckerInterface<any, D>>sourceChecker;
          } else {
            if (!registry.hasChecker(<PrimitiveKeyInterface>sourceChecker)) {
              throw new Error(`Checker with key "${String(sourceChecker)}" not found`);
            }
            checker = registry.getChecker(<PrimitiveKeyInterface>sourceChecker);
          }
          schema = schema.test(
            `${String(this.key)}::${key}::${index}`,
            ({ value }): string => isFunction(checker.errorMessage) ? checker.errorMessage(value, context) : checker.errorMessage,
            (value): Promise<boolean> => checker.check(value, context),
          );
        }
      }

      return schema;
    });
    return yup.object(shape);
  }
}
